import { IonCol, IonGrid, IonIcon, IonRow } from "@ionic/react";
import { SIPrefix, SelectFormatter } from "functions/Formatting";
import { Glob, useLoc }              from "functions/Language";
import { FC, ReactNode, useEffect, useRef, useState } from "react";

import { chevronDownOutline, chevronForwardOutline } from "ionicons/icons";
import { TYPE_PLA } from "functions/networking/NetTypes";


const colTitles    = ["L1", "L2", "L3"];
const colTitlesSum = ["L1", "L2", "L3", "Σ"];
const llTitles     = ["L1 - L2", "L2 - L3", "L3 - L1"];
const llTitlesSum  = ["L1 - L2", "L2 - L3", "L3 - L1", "Σ"];

export enum CollapseLevel {
	mixed    = 0,
	closeAll = 1,
	openSome = 2,
	openAll  = 3,
}

export type DataRow = {
	values?: (number | null)[],
	min   ?: (number | null)[],
	avg   ?: (number | null)[],
	max   ?: (number | null)[],

	maxt  ?: (string | null)[],
	mint  ?: (string | null)[],

	formatter: (val: number) => ReactNode,
	label: ReactNode,
	unit?: string,

	mult?: number,
};

function CombineDataPLA<K extends string, T extends { [k in K]: readonly [number, number, number] }>(data: T, key_phases?: K, key_total?: K) {
	const result : (number | null)[] = [null, null, null, null];

	if (key_total !== undefined) {
		result[0] = data[key_total][0];
	}

	if (key_phases !== undefined) {
		for (let l = 0; l < 3; l++) {
			result[l+1] = data[key_phases][l];
		}
	}

	return result;
}

export function DataRowsFromPLA<
	L extends { [k: string]: ReactNode },
	U extends { [k: string]: string | undefined },
	K extends string,
	T extends { [k in K]: readonly [number, number, number] },
>(
	loc: L,
	units: U,
	rows: { label: (keyof L) & (keyof U) & string, 3?: K, 1?: K }[],
	now: T,
	mam?: { min: T, avg: T, max: T },
) : DataRow[] {
	return rows.map(row => ({
		values: CombineDataPLA(now, row[3], row[1]),
		min   : mam && CombineDataPLA(mam.min, row[3], row[1]),
		avg   : mam && CombineDataPLA(mam.avg, row[3], row[1]),
		max   : mam && CombineDataPLA(mam.max, row[3], row[1]),

		formatter: SelectFormatter(row.label, TYPE_PLA),
		label: loc[row.label],
		unit: units[row.label],
	}));
}


const COL_SUM0 = 1;
const COL_SUM3 = -1;
const COL_3L  = 3;
const COL_3L_SUM = 4;
type Columns = typeof COL_SUM0 | typeof COL_SUM3 | typeof COL_3L | typeof COL_3L_SUM;

function UpgradeColumns(a: Columns | null, b: Columns | null) : Columns | null {
	if (null === a) return b;
	if (null === b) return a;
	if (a > b) return UpgradeColumns(b, a);
	if (a === b) return a;
	if (a === COL_SUM0 || a === COL_SUM3) return COL_3L_SUM;
	return b;
}

function SelectColumnsOfRow<T>(row: (T|undefined|null|"")[]) : Columns | null {
	function No(val : typeof row[number]) { return null === val || undefined === val || "" === val; }

	if (No(row[1]) && No(row[2])) {
		if (No(row[0]) && No(row[3])) return null;
		if (No(row[0])) return COL_3L; // COL_SUM3;
		if (No(row[3])) return COL_SUM0;
		return COL_3L_SUM;
	}

	if (No(row[0])) return COL_3L;
	else return COL_3L_SUM;
}

function SelectColumns(data: DataRow[] | DataRow[][]) : Columns {
	let cols : Columns | null = null;

	if (data.length && Array.isArray(data[0])) {
		for (let grp of data as DataRow[][]) {
			cols = UpgradeColumns(cols, SelectColumns(grp));
			if (COL_3L_SUM === cols) return cols;
		}
	}
	else {
		for (let row of data as DataRow[]) {
			if (row.values) cols = UpgradeColumns(cols, SelectColumnsOfRow(row.values));
			if (row.max)    cols = UpgradeColumns(cols, SelectColumnsOfRow(row.max));
			if (row.maxt)   cols = UpgradeColumns(cols, SelectColumnsOfRow(row.maxt));
			if (row.avg)    cols = UpgradeColumns(cols, SelectColumnsOfRow(row.avg));
			if (row.min)    cols = UpgradeColumns(cols, SelectColumnsOfRow(row.min));
			if (row.mint)   cols = UpgradeColumns(cols, SelectColumnsOfRow(row.mint));
			if (COL_3L_SUM === cols) return cols;
		}
	}

	return cols ?? COL_3L;
}

export const CollapsableTable : FC<{
	data: DataRow[] | DataRow[][],
	title : string,
	LL? : boolean,
	headerFields?: string[],
	collapse?: CollapseLevel,
	onToggle?: () => void,
	margin? : string,
	uncollapsableChildren?: boolean,
	nopad?: boolean,
}> = (props) => {
	const loc = useLoc(mam_glob);

	const [open, setOpen] = useState(props.collapse ?? CollapseLevel.openSome);


	useEffect(function MassCollapse() {
		if (props.collapse !== undefined && props.collapse !== CollapseLevel.mixed) {
			setOpen(props.collapse);
		}
	}, [props.collapse]);

	const columns = SelectColumns(props.data);

	let headerFields;
	if (props.headerFields) headerFields = props.headerFields;
	else {
		switch (columns) {
		case COL_SUM0:
		case COL_SUM3:
			headerFields = loc.values;
			break;
		case COL_3L:
			if (props.LL) headerFields = llTitles;
			else          headerFields = colTitles;
			break;
		case COL_3L_SUM:
			if (props.LL) headerFields = llTitlesSum;
			else          headerFields = colTitlesSum;
			break;
		default: {
			let _ : never = columns;
			console.error("Unhandled columns case", _);
			headerFields = colTitlesSum;
		} break;
		}
	}

	function AdvanceState() {
		props.onToggle?.();
		setOpen(old => {
			switch (old) {
				case CollapseLevel.openAll:  return CollapseLevel.closeAll;
				case CollapseLevel.openSome: return props.uncollapsableChildren ? CollapseLevel.closeAll : CollapseLevel.openAll;
				default:                     return CollapseLevel.openSome;
			}
		});
	}

	return <div style={props.nopad ? undefined : {padding: ".5%"}}>
		<IonGrid class="table">
			<IonRow class="header bheader" onClick={AdvanceState}>
				<IonCol size={COL_SUM0 === columns || COL_SUM3 === columns ? undefined : "3"}>
					{props.collapse !== undefined
					? <CollapseIndicator collapsed={open < CollapseLevel.openSome} title={props.title} />
					: props.title
					}
				</IonCol>
				{headerFields.map((v, i) => <IonCol key={i}>{v}</IonCol>)}
				<IonCol size="auto" style={{padding: "2px"}}><div className="tableUnit"></div></IonCol>
			</IonRow>

			<div style={
				props.collapse === undefined || open >= CollapseLevel.openSome
					? undefined
					: {height: props.margin ?? "0px", visibility: "hidden", overflow: "hidden"}
			}>
				{props.data.map((v, i) => <div key={i} className="bordered">
					{Array.isArray(v)
					? v.map((row, j) => row.values && <CollapsableRow cols={columns} mam_loc={loc} row={row} key={j} {...props} collapse={open} />)
					: v.values && <CollapsableRow cols={columns} mam_loc={loc} row={v} {...props} collapse={open} />
					}
				</div>)}
			</div>
		</IonGrid>
	</div>
}

const CollapseIndicator : FC<{
	title: ReactNode,
	collapsed : boolean,
	color? : string,
}> = (props) => <div style={{display: "flex"}}>
  <IonIcon icon={props.collapsed ? chevronForwardOutline : chevronDownOutline} color={props.color} style={{minWidth: "1em"}} />
  <span style={{flex : 1, position: "relative"}}>{props.title}</span>
</div>

const CollapsableRow : FC<{
	row: DataRow,
	mam_loc : { min : string, avg : string, max : string },
	collapse?: CollapseLevel,
	onToggle?: () => void,
	singlePhase?: boolean,
	cols: Columns,
}> = (props) => {
	const [collapsed, setCollapsed] = useState((props.collapse ?? CollapseLevel.openAll) < CollapseLevel.openAll);
	const worthwhile = props.row.min !== undefined || props.row.avg !== undefined || props.row.max !== undefined;

	useEffect(function MassCollapse() {
		if (props.collapse !== undefined && props.collapse !== CollapseLevel.mixed) {
			setCollapsed(props.collapse < CollapseLevel.openAll);
		}
	}, [props.collapse]);

	const collapsable = props.collapse !== undefined;
	const shared = {
		baseUnit: props.row.unit,
		formatter: props.row.formatter,
		fixedMult: props.row.mult,
		collapsable: collapsable,
		cols: props.cols,
	};

	return <div onClick={() => { if (collapsable) { props.onToggle?.(); setCollapsed(v => !v); } }}>
		<DataRowDisplay label={props.row.label} data={props.row.values} collapser={(collapsable && worthwhile) ? collapsed : undefined} {...shared} />
		<span hidden={collapsable && (!worthwhile || collapsed)}>
			<DataRowDisplay label={props.mam_loc.max} data={props.row.max} times={props.row.maxt} style={{color : "var(--table-color-max)"}} {...shared} />
			<DataRowDisplay label={props.mam_loc.avg} data={props.row.avg}                        style={{color : "var(--table-color-avg)"}} {...shared} />
			<DataRowDisplay label={props.mam_loc.min} data={props.row.min} times={props.row.mint} style={{color : "var(--table-color-min)"}} {...shared} />
		</span>
	</div>
}

const DataRowDisplay : FC<{
	data?: (number | null)[],
	times?: (string | null)[],
	label: ReactNode,
	baseUnit?: string,
	fixedMult?: number,
	collapser? : boolean,
	style? : any,
	collapsable?: boolean,
	formatter: (val: number) => ReactNode,
	cols: Columns,
}> = (props) => {
	const ranges = useRef({ arr: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0], i: 0 });

	if (props.data === undefined || props.data.every(v => isNaN(v as any))) return <></>;

	let times: undefined | string[];
	if (props.times?.some(t => t?.length)) times = props.times.map(t => t ?? "");
	else                                   times = undefined;

	// urceni si prefixu
	let unit: string | undefined, mult: number;
	if (props.fixedMult !== undefined) {
		unit = props.baseUnit;
		mult = props.fixedMult;
	}
	else if (props.baseUnit && props.baseUnit !== "%") {
		const currMax = Math.max(...props.data.map(val => val ? Math.abs(val) : 0));

		ranges.current.arr[ranges.current.i] = currMax;
		ranges.current.i++;
		ranges.current.i %= ranges.current.arr.length;

		const longMax = Math.max(...ranges.current.arr);

		const si = SIPrefix(longMax);
		unit = si.prefix + props.baseUnit;
		mult = si.mult;
	} else {
		unit = props.baseUnit;
		mult = 1;
	}

	const single_phase = COL_SUM3 === props.cols || COL_SUM0 === props.cols;
	let col_indexes : number[] = [1, 2, 3, 0];
	switch (props.cols) {
	case COL_3L: col_indexes = [1, 2, 3]; break;
	case COL_SUM0: col_indexes = [0]; break;
	case COL_SUM3: col_indexes = [3]; break;
	case COL_3L_SUM: col_indexes = [1, 2, 3, 0]; break;
	}

	return <>
		{/* hodnoty */}
		<IonRow style={props.style}>
			<IonCol size={single_phase ? undefined : "3"}>
				{props.collapser === undefined
					? <div style={props.collapsable ? { paddingLeft: "21px" } : undefined}> {props.label} </div>
					: <CollapseIndicator collapsed={props.collapser} color="secondary" title={props.label} />
				}
			</IonCol>

			{col_indexes.map(i =>
				<IonCol key={i}>{undefined !== props.data?.[i] && null !== props.data[i] && <span className="tableNumber">{props.formatter(props.data[i]! * mult)}</span>}</IonCol>
			)}

			<IonCol size="auto"><div className="tableUnit">{unit}</div></IonCol>
		</IonRow>

		{/* casy */}
		{times && <IonRow style={props.style}>
			<IonCol size={single_phase ? undefined : "3"}></IonCol>
			{col_indexes.map((i) => <IonCol key={i}>{times?.[i]}</IonCol>)}
			<IonCol size="auto"><div className="tableUnit"></div></IonCol>
		</IonRow>}
	</>
}


const mam_glob = Glob({
	en: {
		min: "min",
		avg: "average",
		max: "max",

		values: ["Values"],
	},
	cs: {
		min: "min",
		avg: "průměr",
		max: "max",

		values: ["Hodnoty"],
	},
	pg: {
		min: "min",
		avg: "média",
		max: "max",

		values: ["Valores"],
	},
	es: {
		min: "min",
		avg: "media",
		max: "max",

		values: ["Valores"],
	},
} as const);

